import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { or } from 'ember-truth-helpers';
import DirectUploader from 'garaje/components/direct-uploader';
import LineClamp from 'garaje/components/line-clamp';
import UiButton from 'garaje/components/ui-button';
import dateFormat from 'garaje/helpers/date-format';
import { trackedReset } from 'tracked-toolbox';
import type { FileUpload, ValidationResult } from 'utils/direct-uploader';

const PDF_FILE_SIZE_LIMIT_IN_BYTES = 10000000;

interface PdfUploaderSignature {
  Args: {
    deleteDisabled?: boolean;
    disabled?: boolean;
    inputId?: string;
    onPendingUpload: (upload: FileUpload | undefined) => void;
    onValidate?: (message: string | null) => void;
    reset?: () => void;
    translationLanguagesAvailable?: boolean;
    upload: FileUpload | null;
    uploaderDisabled: boolean;
    useStandaloneFileUpload?: boolean;
  };
  Blocks: {
    error: [message: string];
  };
  Element: HTMLElement;
}

export default class PdfUploader extends Component<PdfUploaderSignature> {
  @trackedReset('args.upload') errorMessage: string | null = null;

  @action
  reset(upload: FileUpload | undefined): void {
    this.args.reset?.();
    upload?.reset();
  }

  @action
  updateValidity(result: ValidationResult): void {
    if (!result.sizeValid)
      this.errorMessage = 'The file must be 10mb or less. Consider compressing the PDF to reduce its file size.';
    else if (!result.typeValid) this.errorMessage = 'The file must be a PDF.';
    else this.errorMessage = null;

    this.args.onValidate?.(this.errorMessage);
  }

  <template>
    <DirectUploader
      @validSize={{PDF_FILE_SIZE_LIMIT_IN_BYTES}}
      @validType="application/pdf"
      @accept="application/pdf"
      @onPendingUpload={{@onPendingUpload}}
      @onValidate={{this.updateValidity}}
      @currentUpload={{@upload}}
      @enqueue={{false}}
      @showResetButton={{false}}
      @showErrorState={{false}}
      @disabled={{or @disabled @uploaderDisabled}}
      @inputId={{@inputId}}
      @label={{if @disabled "No PDF attachments"}}
      @subLabel={{if @disabled ""}}
      @useStandaloneFileUpload={{@useStandaloneFileUpload}}
      class="{{if @uploaderDisabled 'h-24 !border-solid' 'h-40'}} {{if this.errorMessage 'border-papaya-50'}}"
      ...attributes
    >
      <:previewing as |upload forceUpload|>
        <div class="flex h-full w-full items-center">
          <div class="w-20 h-full bg-carbon-5 mr-6 flex items-center justify-center">
            {{svgJar "document" height="24"}}
          </div>
          <span class="flex-1 font-medium text-carbon-70 overflow-hidden" data-test-pdf-filename>
            <LineClamp @lines={{3}} @text={{upload.file.name}} @useNative={{true}} />
          </span>
          <span class="font-normal {{if upload.document 'text-carbon-70' 'text-carbon-50'}}" data-test-uploaded-date>
            {{#if upload.document}}
              Uploaded
              {{dateFormat upload.document.updated-at "MMM dd, yyyy"}}
            {{else}}
              Not yet uploaded
            {{/if}}
          </span>
          {{#unless @disabled}}
            <UiButton data-test-replace-file {{on "click" forceUpload}} class="ml-6 btn--link !text-gem-50">
              Replace PDF
            </UiButton>
            <span class="ml-6">
              <UiButton data-test-delete-file {{on "click" (fn this.reset upload)}} disabled={{@deleteDisabled}}>
                {{svgJar "trash-delete-black" height="16" width="16"}}
              </UiButton>
              {{#if @deleteDisabled}}
                <EmberTooltip
                  @side="left"
                  @tooltipClass="rounded-lg !p-3"
                  @text="Document cannot be empty. Please replace the PDF or add text."
                />
              {{/if}}
            </span>
          {{/unless}}
        </div>
      </:previewing>
    </DirectUploader>
    {{#if this.errorMessage}}
      {{yield this.errorMessage to="error"}}
    {{/if}}
  </template>
}
